import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { PageSection } from "@/components/site/PageSection";
import {
  Callout,
  CurrencyNote,
  FaqList,
  H2,
  H3,
  InsuranceCTA,
  P,
  SITE,
  SpokeCards,
  UL,
  faqSchema,
  medicalWebPageSchema,
  type Faq,
} from "@/components/site/InsuranceProse";
import { TelehealthCta } from "@/components/site/TelehealthCta";

const TITLE =
  "GLP-1 Denials & Cash-Pay Prices | Virginia Bariatric";
const DESCRIPTION =
  "What to do after a Zepbound or Wegovy denial, how Virginia's external review works, and current LillyDirect and NovoCare self-pay prices.";
const URL = `${SITE}/medical-weight-loss/insurance/cash-pay-denials`;
const NAME = "Weight-Loss Medication Denials and Cash-Pay Pricing";

const FAQS: Faq[] = [
  {
    q: "What should I do if I am denied?",
    a: "Find out which gate you failed. A benefit exclusion denial cannot be fixed by clinical appeal, so cash-pay is the realistic path. A clinical-criteria denial often can be fixed: submit missing lifestyle documentation, recent height and weight, or an overlooked comorbidity; check whether a different formulation (pen versus KwikPen versus vial) is the covered one; file an internal appeal and then external review; and ask your prescriber for a peer-to-peer review.",
  },
  {
    q: "What does weight-loss medication cost if I pay cash?",
    a: "Manufacturer direct-pay programs have become a genuine alternative. Zepbound self-pay through LillyDirect and major pharmacies starts around $299/month for 2.5 mg, $399 for 5 mg, and $449 for higher doses. Wegovy through NovoCare Pharmacy starts at $199/month for the two lowest doses for new patients, then $349/month, with the pill starting at $149/month. Self-pay dollars do not count toward your deductible or out-of-pocket maximum.",
  },
];

export const Route = createFileRoute("/medical-weight-loss/insurance/cash-pay-denials")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss/insurance/cash-pay-denials") : []),
    ],
    scripts: import.meta.env.SSR ? [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS, URL)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          medicalWebPageSchema({ url: URL, name: NAME, description: DESCRIPTION })
        ),
      },
    ] : [],
  }),
  component: DenialsCashPage,
});

function DenialsCashPage() {
  return (
    <>
      <PageSection className="pt-8 pb-4 text-center">
        <h1 className="text-3xl md:text-5xl font-serif">Denials and Cash-Pay Options</h1>
        <p className="mt-4 max-w-3xl mx-auto text-muted-foreground leading-[1.6]">
          How to respond to a denial, what self-pay actually costs today, and exactly what to ask
          your plan.
        </p>
        <TelehealthCta />

      </PageSection>

      <PageSection maxWidth="4xl" className="pb-4">
        <CurrencyNote />
        <P>
          Before appealing, confirm which of the{" "}
          <Link
            to="/medical-weight-loss/insurance"
            preload="intent"
            className="text-brand-deep underline hover:no-underline"
          >
            two coverage gates
          </Link>{" "}
          stopped you &mdash; it determines whether an appeal can work at all.
        </P>

        <H2 id="denied">If you&rsquo;re denied</H2>
        <P>
          <strong>Find out which gate you failed.</strong> A denial for &ldquo;benefit
          exclusion&rdquo; means your plan doesn&rsquo;t cover this category at all &mdash; appealing
          the clinical criteria won&rsquo;t help. A denial for not meeting clinical criteria can
          often be fixed.
        </P>
        <UL>
          <li>
            <strong>Get the denial in writing and read the specific reason.</strong> Common fixable
            reasons include missing documentation of lifestyle attempts, missing height and weight
            from within the required window, a comorbidity that wasn&rsquo;t submitted, or an
            undocumented step-therapy requirement.
          </li>
          <li>
            <strong>Ask about the backdoor.</strong> Sleep apnea, type 2 diabetes, cardiovascular
            disease, or MASH may qualify you under a pathway weight-loss exclusions don&rsquo;t
            govern.
          </li>
          <li>
            <strong>Check the formulation.</strong> Pen versus KwikPen versus vial can be the entire
            problem.
          </li>
          <li>
            <strong>File an internal appeal, then an external review.</strong> In Virginia, external
            review is available through the State Corporation Commission Bureau of Insurance for
            state-regulated plans; self-funded employer plans follow federal rules.
          </li>
          <li>
            <strong>Have your prescriber call.</strong> A peer-to-peer review often resolves denials
            that paperwork alone doesn&rsquo;t.
          </li>
        </UL>

        <H2 id="cash">If you&rsquo;re paying cash</H2>
        <P>
          Manufacturer direct-pay programs have become a genuine alternative, particularly for people
          whose plans exclude these drugs.
        </P>
        <P>
          <strong>Zepbound.</strong> Lilly offers self-pay pricing on the KwikPen through LillyDirect
          and, as of March 2026, at major pharmacies nationwide &mdash; starting at $299/month for
          2.5 mg, $399 for 5 mg, and $449 for higher doses, with prices tied to refilling within a
          set window.
        </P>
        <P>
          <strong>Wegovy.</strong> Through NovoCare Pharmacy, self-pay pricing for the Wegovy pen
          starts at $199/month for the two lowest doses for new patients (a limited-time rate through
          December 31, 2026), then $349/month. Wegovy HD is $399/month; the Wegovy pill starts at
          $149/month for certain doses. Patients with commercial insurance may pay as little as
          $25/month through the savings offer, subject to a $100/month maximum savings.
        </P>
        <Callout>
          <strong>Two important limits on savings cards.</strong> Money spent through self-pay
          programs does <strong>not</strong> count toward your deductible or out-of-pocket maximum.
          And people with government insurance &mdash; Medicare, Medicaid, TRICARE &mdash; generally
          cannot use commercial copay cards, though they may pay the self-pay price outside their
          insurance. Novo Nordisk notes that FEHB, marketplace, and state employee plans are{" "}
          <strong>not</strong> treated as government programs for purposes of its savings offer.
        </Callout>
        <P>Prices and program terms change often. Verify current pricing directly with the manufacturer.</P>
        <P>
          If long-term medication cost is the obstacle, it&rsquo;s worth comparing against{" "}
          <Link to="/services" className="text-brand-deep underline hover:no-underline">
            surgical weight-loss options
          </Link>
          , which most Virginia plans do cover.
        </P>

        <H2 id="questions">Questions to ask your plan</H2>
        <P>Call the member services number on your card and ask:</P>
        <ol className="mt-3 list-decimal space-y-2 pl-6 text-foreground/85 leading-[1.6]">
          <li>
            Does my plan cover prescription weight-loss medications at all, or are they excluded? Ask
            them to state it clearly.
          </li>
          <li>Is Zepbound, Wegovy, or Saxenda on my formulary, and what tier?</li>
          <li>What BMI does my plan require, and does a weight-related condition lower that threshold?</li>
          <li>How many months of documented lifestyle changes do I need, and what counts as documentation?</li>
          <li>Do I have to try a different weight-loss drug first?</li>
          <li>Which formulation is covered &mdash; pen, KwikPen, or vial?</li>
          <li>
            If weight-loss drugs are excluded, is the same medication covered for another condition I
            have &mdash; sleep apnea, type 2 diabetes, heart disease, or fatty liver disease?
          </li>
          <li>How long does an initial approval last, and what do I need to show to renew?</li>
          <li>What&rsquo;s my actual monthly cost after the deductible?</li>
        </ol>

        <H3>Let us help</H3>
        <P>
          Our staff verifies benefits and submits prior authorizations every week, and we know which
          documentation each Virginia plan actually wants.{" "}
          <Link to="/contact" className="text-brand-deep underline hover:no-underline">
            Contact our office
          </Link>{" "}
          and we&rsquo;ll walk through your plan with you.
        </P>

        <H2 id="faqs">Frequently asked questions</H2>
        <FaqList faqs={FAQS} />

        <H2 id="more">Other coverage guides</H2>
        <SpokeCards exclude="/medical-weight-loss/insurance/cash-pay-denials" />
      </PageSection>

      <InsuranceCTA />
    </>
  );
}
